import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./EventPhotosMarquee.css";

const HEADING = "Event Highlights";
const SUBCOPY =
  "Moments from our meetups, workshops and hackathons. This is what building Web3 together actually looks like.";

/* Drop event photos into src/assets/images/events and they show up here —
   no code change needed. Files load in filename order. */
const photoContext = require.context(
  "../../assets/images/events",
  false,
  /\.(png|jpe?g|webp|avif)$/
);

/* Lightbox copy. Key is the filename — add an entry to override the title and
   caption for a photo; anything missing falls back to the filename. */
const CAPTIONS = {
  "01-venture-connect-fireside.jpg": {
    title: "Venture Connect — Fireside",
    caption: "Founders and investors trading notes on stage.",
  },
  "02-venture-connect-audience.jpg": {
    title: "Venture Connect — Audience",
    caption: "A full room for the opening session.",
  },
  "03-venture-connect-crowd.jpg": {
    title: "Venture Connect — The Floor",
    caption: "Conversations spilling out between talks.",
  },
  "04-venture-connect-networking.jpg": {
    title: "Venture Connect — Networking",
    caption: "Where most of the deals actually start.",
  },
  "05-venture-connect-group.jpg": {
    title: "Venture Connect — The Crew",
    caption: "The community that showed up to build.",
  },
};

/* "./03-venture-connect-crowd.jpg" -> "Venture Connect Crowd" */
const titleFromKey = (key) =>
  key
    .replace(/^\.\//, "")
    .replace(/\.[^.]+$/, "")
    .replace(/^\d+[-_]/, "")
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const PHOTOS = photoContext
  .keys()
  .sort()
  .map((key) => {
    const name = key.replace(/^\.\//, "");
    const copy = CAPTIONS[name] || {};

    return {
      key,
      src: photoContext(key),
      title: copy.title || titleFromKey(key),
      caption: copy.caption || "",
    };
  });

/* The marquee needs enough cards to cover a wide screen, so a short list is
   cycled until it does. Once there are 8+ real photos nothing repeats. */
const MIN_CARDS = 8;
const TRACK = Array.from(
  { length: Math.max(MIN_CARDS, PHOTOS.length) },
  (_, index) => PHOTOS[index % PHOTOS.length]
);

const PhotoCard = ({ photo, onOpen }) => (
  <figure className="relative rounded-md overflow-hidden w-[440px] md:w-[520px] aspect-[3/2] shrink-0 bg-[#e6e4e0] m-0">
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open event photo"
      className="group/card absolute inset-0 w-full h-full p-0 border-0 bg-transparent cursor-pointer"
    >
      <img
        src={photo.src}
        alt="The Crypto Hub event"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
      />
      <span className="absolute inset-0 bg-[#1b0a44]/0 group-hover/card:bg-[#1b0a44]/35 transition-colors duration-300" />
      <span className="material-symbols-outlined absolute inset-0 flex items-center justify-center text-white text-[40px] opacity-0 scale-90 group-hover/card:opacity-100 group-hover/card:scale-100 transition-all duration-300">
        zoom_in
      </span>
    </button>
  </figure>
);

const STEP_BUTTON_CLASS =
  "w-11 h-11 inline-flex items-center justify-center rounded-full bg-black/55 " +
  "border border-white/25 !text-white hover:bg-white hover:!text-[#1c2220] " +
  "transition-colors duration-200 cursor-pointer";

const StepButton = ({ delta, onStep, className = "" }) => (
  <button
    type="button"
    onClick={() => onStep(delta)}
    aria-label={delta < 0 ? "Previous photo" : "Next photo"}
    className={`${STEP_BUTTON_CLASS} ${className}`}
  >
    <span className="material-symbols-outlined text-[22px]">
      {delta < 0 ? "chevron_left" : "chevron_right"}
    </span>
  </button>
);

/* Full-screen viewer. Arrow keys and the side buttons walk the unique photo
   list, not the padded marquee track, so nothing repeats while browsing. */
const Lightbox = ({ index, onClose, onStep }) => {
  const photo = PHOTOS[index];

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onStep(1);
      if (event.key === "ArrowLeft") onStep(-1);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, onStep]);

  /* Portalled to <body> so no ancestor transform traps the fixed overlay
     behind the site header.

     `!` on the overlay's flex utilities: legacy stylesheets (Blogs.css,
     HomeProjects.css, …) redefine a global `.flex` as `space-between` +
     `wrap`, which pins the panel to the left edge. */
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Event photo"
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex !flex-nowrap !items-center !justify-center bg-black/80 backdrop-blur-sm p-0 md:p-8 eventPhotosLightboxIn"
    >
      {/* Edge-to-edge on phones: the panel drops its side padding so the photo
          spans the full viewport width, and the text rows carry their own. */}
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-[#1c2220] rounded-none md:rounded-2xl px-0 py-5 md:px-10 md:py-9 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-6 px-4 md:px-0">
          <h3 className="font-headline font-bold text-white text-xl md:text-2xl leading-snug m-0">
            {photo.title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-11 h-11 inline-flex items-center justify-center rounded-full bg-black/55 border border-white/25 !text-white hover:bg-white hover:!text-[#1c2220] transition-colors duration-200 cursor-pointer"
          >
            <span className="material-symbols-outlined text-[22px]">close</span>
          </button>
        </div>

        {/* 2px of breathing room a side on phones — near edge to edge, but the
            photo no longer bleeds into the viewport border. */}
        <div className="relative mt-4 md:mt-6 px-0.5 md:px-0">
          <img
            src={photo.src}
            alt={photo.title}
            className="w-full max-h-[62vh] object-contain rounded-none md:rounded-md bg-black"
          />
          {/* Desktop: floating over the photo's sides. */}
          {PHOTOS.length > 1 && (
            <>
              <StepButton
                delta={-1}
                onStep={onStep}
                className="hidden md:inline-flex absolute left-3 top-1/2 -translate-y-1/2"
              />
              <StepButton
                delta={1}
                onStep={onStep}
                className="hidden md:inline-flex absolute right-3 top-1/2 -translate-y-1/2"
              />
            </>
          )}
        </div>

        {/* Phones only: a row under the photo, one button at each end.
            `[display:flex]` rather than `flex` — the legacy global `.flex`
            (Blogs.css, HomeProjects.css, …) is emitted after Tailwind and
            would beat `md:hidden`, leaving this row visible on desktop too. */}
        {PHOTOS.length > 1 && (
          <div className="[display:flex] md:hidden !flex-nowrap !justify-between items-center gap-4 px-4 mt-3">
            <StepButton delta={-1} onStep={onStep} />
            <StepButton delta={1} onStep={onStep} />
          </div>
        )}

        <div className="flex items-end justify-between gap-6 mt-2 md:mt-5 px-4 md:px-0">
          <p className="font-headline text-white/70 text-sm md:text-base m-0">
            {photo.caption}
          </p>
          {PHOTOS.length > 1 && (
            <p className="font-headline text-white/40 text-sm shrink-0 m-0">
              {index + 1} / {PHOTOS.length}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const EventPhotos = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const step = useCallback(
    (delta) =>
      setOpenIndex(
        (current) => (current + delta + PHOTOS.length) % PHOTOS.length
      ),
    []
  );

  if (PHOTOS.length === 0) return null;

  return (
    <section id="event-photos" className="w-full bg-white py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-10">
        <div className="text-center">
          <h2 className="font-headline font-bold tracking-tight text-[#111113] text-[clamp(1.75rem,3.4vw,2.5rem)] leading-[1.12] m-0">
            {HEADING}
          </h2>
          <p className="font-headline text-[#4a4753] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mt-4 mb-0">
            {SUBCOPY}
          </p>
        </div>
      </div>

      {/* Full-bleed marquee, right to left. Listed twice so it loops seamlessly. */}
      <div className="group relative mt-14 eventPhotosMarqueeMask">
        <div className="flex !flex-nowrap w-max eventPhotosMarqueeTrack group-hover:[animation-play-state:paused]">
          {[...TRACK, ...TRACK].map((photo, index) => (
            <PhotoCard
              key={`${photo.key}-${index}`}
              photo={photo}
              onOpen={() => setOpenIndex(PHOTOS.indexOf(photo))}
            />
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <Lightbox index={openIndex} onClose={close} onStep={step} />
      )}
    </section>
  );
};

export default EventPhotos;
